import { EDIT_ITEM } from "constants/index"
import { getUpdatedStates } from "utils/localCacheStateUtils"
import { AppDispatch, RootState } from "appRedux/store"
import { updateUserWishItems, updateUserWishLists, updateUserWishListsWithFirstFourItems } from "appRedux/slices/userSlice"
import { updateViewedUserWishItems, updateViewedUserWishLists, updateViewedUserWishListsWithFirstFourItems } from "appRedux/slices/viewedUserSlice"
import { userProfileApi } from "appRedux/slices/userApiSlice"
import { wishItemApi } from "appRedux/slices/wishItemApiSlice"
import { wishListApi } from "appRedux/slices/wishListApiSlice"
import { updateFeeds } from "appRedux/slices/feedSlice"
import { updatePopularItems } from "appRedux/slices/wishItemSlice"
import {
  updateCurrentItemComments,
  updateCurrentItemInfo,
  updateCurrentItemLikes,
  updateGenies,
  updateWishListInfo
} from "appRedux/slices/wishListSlice"

export async function handleOnGetWishItemsQueryStarted(arg: void, { dispatch, queryFulfilled, getState }: any) {
  try {
    const { data } = await queryFulfilled
    const state = getState() as RootState
    const loggedInUserProfile = state.user?.profile
    const viewedUserProfile = state.viewedUser?.profile

    dispatch(updateUserWishItems(data))

    if (loggedInUserProfile?.handle === viewedUserProfile?.handle) {
      dispatch(updateViewedUserWishItems(data))
    }
  } catch (error) {
    console.error("Error getting wish items.", error)
  }
}

export async function handleOnEditWishItemQueryStarted({ id, body }: any, { dispatch, queryFulfilled, getState }: any) {
  try {
    await queryFulfilled
    const state = getState() as RootState
    const userProfile = state.user.profile
    const viewedUser = state.viewedUser
    const wishlistsWithFirstFourItems = state.user.wishListsWithFirstFourItems || []
    const wishItems = state.user.wishItems || []
    const wishLists = state.user.wishLists
    const list = state?.wishList?.listInfo
    const item = state?.wishList?.itemInfo
    const currentItemIndex = state?.wishList?.currentItemIndex
    if (!item || !userProfile) return
    let objectBody = Object.fromEntries(body.entries()) as WishItemRequestPayload
    const { wishlistIds, ...restItemInfo } = objectBody
    const filteredLists = wishLists?.filter(l => wishlistIds.includes(l.id)).map(l => ({
      ...l,
      itemCount: null,
      listItems: null
    }))
    const { listInfo, userProfileLists, userWishLists, itemInfo, userWishItems } = getUpdatedStates(
      EDIT_ITEM,
      wishlistsWithFirstFourItems,
      list,
      { ...item, ...objectBody, wishlists: filteredLists, id: Number(id) },
      wishItems
    )

    dispatch(updateUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))
    dispatch(updateUserWishLists({ wishlists: userWishLists }))
    dispatch(updateUserWishItems({ items: userWishItems }))

    if (listInfo?.listItems) {
      if (currentItemIndex >= listInfo.listItems.length) {
        dispatch(updateCurrentItemInfo({ ...listInfo.listItems[currentItemIndex - 1], wishlists: [] }))
      } else {
        dispatch(updateCurrentItemInfo({ ...listInfo.listItems[currentItemIndex], wishlists: [] }))
      }
    } else {
      dispatch(updateCurrentItemInfo(null))
    }

    if (state.user.profile?.handle) {
      dispatch(userProfileApi.util.updateQueryData("getWishListsWithFirstFourItems", state.user.profile?.handle, (prevData) => {
        prevData.wishlists = userProfileLists
      }))
    }
    dispatch(wishListApi.util.updateQueryData("getWishLists", undefined, (prevData) => {
      prevData.wishlists = userWishLists
    }))
    if (list?.id && listInfo) {
      dispatch(wishListApi.util.updateQueryData("getWishList", Number(list.id), (prevData) => {
        prevData.listItems = listInfo.listItems || []
        prevData.itemCount = listInfo.itemCount
      }))
    }
    dispatch(wishItemApi.util.updateQueryData("getWishItem", Number(id), (prevData) => {
      Object.assign(prevData, itemInfo)
    }))
    dispatch(wishItemApi.util.updateQueryData("getWishItems", undefined, (prevData) => {
      if (!prevData.items) return
      prevData.items = userWishItems
    }))

    if (viewedUser) {
      if (viewedUser.wishListsWithFirstFourItems) {
        dispatch(updateViewedUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))
      }
      if (viewedUser.wishLists) {
        dispatch(updateViewedUserWishLists({ wishlists: userWishLists }))
      }
    }
    if (list?.id) {
      dispatch(updateWishListInfo(listInfo))
    }
  } catch (error) {
    console.error("Error editing item:", error)
  }
}

export async function handleOnGetCommentsQueryStarted(id: number, { dispatch, queryFulfilled, getState }: any) {
  try {
    const { data } = await queryFulfilled
    dispatch(updateCurrentItemComments(data))
  } catch (error) {
    console.error(`Error getting comments for item with id ${id}. Error:`, error)
  }
}

export async function handleOnAddCommentQueryStarted({ id, body }: { id: number, body: WishItemCommentRequestPayload }, {
  dispatch,
  queryFulfilled,
  getState
}: any) {
  try {
    await queryFulfilled
    const state = getState() as RootState
    const feeds = state.feed?.feeds || []
    const userProfile = state.user.profile
    const currentItemComments = state.wishList.currentItemComments
    const item = state?.wishList?.itemInfo
    if (!userProfile || !item) return
    const addedComment = {
      ...body,
      authorName: userProfile.handle,
      avatar: userProfile.avatar,
      createdAt: new Date()
    }
    const updatedComments = currentItemComments?.comments ? [...currentItemComments.comments, addedComment] : [addedComment]

    dispatch(updateCurrentItemComments({ comments: updatedComments }))
    dispatch(updateCurrentItemInfo({
      ...item,
      commentCount: item.commentCount + 1
    }))

    dispatch(wishItemApi.util.updateQueryData("getComments", id, (prevData) => {
      prevData.comments.push(addedComment)
    }))

    incrementCommentsCountInFeedCard(id, feeds, dispatch)
  } catch (error) {
    console.error("Error adding comment:", error)
  }
}

export async function handleOnGetLikesQueryStarted(id: number, { dispatch, queryFulfilled, getState }: any) {
  try {
    const { data } = await queryFulfilled
    dispatch(updateCurrentItemLikes(data))
  } catch (error) {
    console.error(`Error getting likes for item with id ${id}. Error:`, error)
  }
}

export async function handleOnMakeWishComeTrueQueryStarted({ id, genieIds }: { id: number, genieIds: number[] }, {
  dispatch,
  queryFulfilled,
  getState
}: any) {
  try {
    await queryFulfilled
    const state = getState() as RootState
    const userProfile = state.user.profile
    const wishlistsWithFirstFourItems = state.user.wishListsWithFirstFourItems || []
    const wishItems = state.user.wishItems || []
    const list = state?.wishList?.listInfo
    const item = state?.wishList?.itemInfo
    if (!item || !userProfile) return
    const { listInfo, userProfileLists, userWishLists, itemInfo, userWishItems } = getUpdatedStates(
      EDIT_ITEM,
      wishlistsWithFirstFourItems,
      list,
      { ...item, cameTrue: true },
      wishItems
    )

    dispatch(updateUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))
    dispatch(updateCurrentItemInfo(itemInfo))
    dispatch(updateUserWishItems({ items: userWishItems }))

    if (state.user.profile?.handle) {
      dispatch(userProfileApi.util.updateQueryData("getWishListsWithFirstFourItems", state.user.profile?.handle, (prevData) => {
        prevData.wishlists = userProfileLists
      }))
    }
    dispatch(wishItemApi.util.updateQueryData("getWishItem", id, (prevData) => {
      prevData.cameTrue = true
    }))
    dispatch(wishItemApi.util.updateQueryData("getWishItems", undefined, (prevData) => {
      if (!prevData.items) return
      prevData.items = userWishItems
    }))
    dispatch(wishItemApi.util.invalidateTags(["Genies"]))

    if (list?.id) {
      dispatch(updateWishListInfo(listInfo))
    }
  } catch (error) {
    console.error(`Error making wish true with id ${id} and genieIds: ${genieIds}.`, error)
  }
}

export async function handleOnRollbackTrueWishQueryStarted(id: number, { dispatch, queryFulfilled, getState }: any) {
  try {
    await queryFulfilled
    const state = getState() as RootState
    const userProfile = state.user.profile
    const wishlistsWithFirstFourItems = state.user.wishListsWithFirstFourItems || []
    const wishItems = state.user.wishItems || []
    const list = state?.wishList?.listInfo
    const item = state?.wishList?.itemInfo
    if (!item || !userProfile) return
    const { listInfo, userProfileLists, userWishLists, itemInfo, userWishItems } = getUpdatedStates(
      EDIT_ITEM,
      wishlistsWithFirstFourItems,
      list,
      { ...item, cameTrue: false },
      wishItems
    )

    dispatch(updateUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))
    dispatch(updateCurrentItemInfo(itemInfo))
    dispatch(updateUserWishItems({ items: userWishItems }))

    if (state.user.profile?.handle) {
      dispatch(userProfileApi.util.updateQueryData("getWishListsWithFirstFourItems", state.user.profile?.handle, (prevData) => {
        prevData.wishlists = userProfileLists
      }))
    }
    dispatch(wishItemApi.util.updateQueryData("getWishItem", id, (prevData) => {
      prevData.cameTrue = true
    }))
    dispatch(wishItemApi.util.updateQueryData("getWishItems", undefined, (prevData) => {
      if (!prevData.items) return
      prevData.items = userWishItems
    }))
    dispatch(wishItemApi.util.invalidateTags(["Genies"]))
    if (list?.id) {
      dispatch(updateWishListInfo(listInfo))
    }
  } catch (error) {
    console.error(`Error rolling back true wish with id ${id}.`, error)
  }
}

export async function handleOnGetWishItemGeniesQueryStarted(id: number, { dispatch, queryFulfilled, getState }: any) {
  try {
    const { data } = await queryFulfilled
    dispatch(updateGenies({ id, genies: data.genies }))
  } catch (error) {
    console.error(`Error getting genies for wish item with id ${id}.`, error)
  }
}

export async function handleOnGetPopularItemsQueryStarted(arg: void, { dispatch, queryFulfilled, getState }: any) {
  try {
    const { data } = await queryFulfilled
    if (!data.items) return
    dispatch(updatePopularItems(data.items))
  } catch (error) {
    console.error("Error getting popular wish items.", error)
  }
}

function incrementCommentsCountInFeedCard(id: number, feeds: WishamFeed[], dispatch: AppDispatch) {
  if (feeds?.length) {
    const feedsCpy = structuredClone(feeds)
    for (const feedCard of feedsCpy) {
      if (feedCard.hasOwnProperty("item") && (feedCard as NewItemFeed)?.item?.id == id) {
        (feedCard as NewItemFeed).item.commentsCount += 1
        dispatch(updateFeeds(feedsCpy))
        break
      }
    }
  }
}
